// Subscriber-scoped chit view: one subscriber's instalments + payment history
// for a single chit, with per-month payment actions (UPI link / manual record
// with optional screenshot). Reached from the subscriber-detail chit rows.

import { useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';
import { current_language } from '../i18n/index.js';
import { format_inr } from '../lib/format.js';
import { orpc } from '../lib/orpc.js';
import { friendly_error } from '../lib/friendly-error.js';
import { ledger_status_label, ledger_month_label } from '../lib/ledger-format.js';
import { MarkPaidModal } from '../components/MarkPaidModal.js';
import { NotifyMenu } from '../components/NotifyMenu.js';
import { useAuth } from '../auth.js';

interface Subscriber { id: string; name_ta: string; name_en: string; phone: string }
interface ChitLite { id: string; name_ta: string; name_en: string; pool_size: number }
interface Cell {
  due_month: string;
  subscription_id: string | null;
  status: 'pending' | 'paid' | 'partial' | 'overdue' | 'no_row';
  expected_amount_paise: number;
  total_paid_paise: number;
}
interface MatrixRow { subscriber: Subscriber | null; slot: number; cells: Cell[] }
interface Payment {
  payment_id: string;
  amount_paise: number;
  method: string;
  reference: string | null;
  timestamp: string | Date;
  due_month: string;
  screenshot_url: string | null;
}

export function SubscriberChitDetailPage(): ReactElement {
  const lang = current_language();
  const nav = useNavigate();
  const qc = useQueryClient();
  const params = useParams<{ subscriber_id: string; chit_id: string }>();
  const subscriber_id = params.subscriber_id ?? '';
  const chit_id = params.chit_id ?? '';
  const { session } = useAuth();
  const is_admin = !!session?.scopes.includes('admin');
  const t = (ta: string, en: string): string => (lang === 'ta' ? ta : en);
  const [pay_target, set_pay_target] = useState<{
    subscription_id: string;
    expected: number;
    paid: number;
    name: string;
  } | null>(null);

  const matrix_q = useQuery({
    queryKey: ['ledger-matrix', chit_id],
    queryFn: () => orpc.ledger.get_matrix({ chit_id }),
    enabled: !!chit_id,
  });
  const hist_q = useQuery({
    queryKey: ['payment-history', subscriber_id, chit_id],
    queryFn: () => orpc.subscribers.payment_history({ subscriber_id, chit_id }),
    enabled: !!subscriber_id && !!chit_id,
  });

  const gen_m = useMutation({
    mutationFn: (target_month: string) => orpc.ledger.generate_cycle({ chit_id, target_month }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['ledger-matrix', chit_id] }),
  });

  if (matrix_q.isLoading) return <div className="page full"><div style={{ color: 'var(--ink-3)' }}>Loading…</div></div>;
  if (matrix_q.error) return <div className="page full"><div style={{ color: 'var(--bad)' }}>{friendly_error(matrix_q.error)}</div></div>;
  if (!matrix_q.data) return <div className="page full" />;

  const chit = matrix_q.data.chit as ChitLite;
  const rows = (matrix_q.data.rows ?? []) as MatrixRow[];
  const row = rows.find((r) => r.subscriber?.id === subscriber_id) ?? null;
  const sub = row?.subscriber ?? null;
  const cells = row?.cells ?? [];
  const payments = (hist_q.data?.payments ?? []) as Payment[];

  const past = cells.filter((c) => c.status !== 'no_row');
  const expected = past.reduce((s, c) => s + c.expected_amount_paise, 0);
  const paid = past.reduce((s, c) => s + c.total_paid_paise, 0);
  const outstanding = Math.max(0, expected - paid);

  return (
    <div className="page full">
      <div className="page-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <button onClick={() => nav(`/subscribers/${subscriber_id}`)} style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', color: 'var(--ink-3)', fontSize: 12, font: 'inherit' }}>
          ← {t('உறுப்பினர்', 'Member')}
        </button>
        <NotifyMenu
          subscriber_id={subscriber_id}
          intent="due_reminder"
          context={{ chit_id }}
          variant="inline"
        />
      </div>

      {!row ? (
        <div className="card" style={{ padding: 22, color: 'var(--ink-3)' }}>
          {t('இந்தச் சீட்டில் உறுப்பினர் இல்லை', 'Not a member of this chit.')}
        </div>
      ) : (
        <>
          <div className="profile-head" style={{ marginBottom: 14 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className={'h-title ' + (lang === 'ta' ? 'tamil' : '')} style={{ fontSize: 18 }}>
                {lang === 'ta' ? sub?.name_ta : sub?.name_en}
              </div>
              <div className="h-sub">
                <span className={lang === 'ta' ? 'tamil' : ''}>{lang === 'ta' ? chit.name_ta : chit.name_en}</span>
                {' · '}
                <span className="mono">#{row.slot}</span>
                {sub?.phone ? (
                  <>
                    {' · '}
                    <span className="mono">{sub.phone}</span>
                  </>
                ) : null}
              </div>
            </div>
          </div>

          <div className="kpis" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
            <div className="kpi">
              <div className="kpi-label">{t('செலுத்தியது', 'Paid')}</div>
              <div className="kpi-value">{format_inr(paid)}</div>
            </div>
            <div className="kpi">
              <div className="kpi-label">{t('எதிர்பார்ப்பு', 'Expected')}</div>
              <div className="kpi-value">{format_inr(expected)}</div>
            </div>
            <div className={'kpi' + (outstanding > 0 ? ' warn' : '')}>
              <div className="kpi-label">{t('நிலுவை', 'Outstanding')}</div>
              <div className="kpi-value">{format_inr(outstanding)}</div>
            </div>
          </div>

          {/* Per-month instalments */}
          <div className="section-band">{t('மாதவாரிக் கட்டணம்', 'Monthly instalments')}</div>
          <div className="card" style={{ padding: 0 }}>
            <table className="filings-table">
              <thead>
                <tr>
                  <th>{t('மாதம்', 'Month')}</th>
                  <th>{t('நிலை', 'Status')}</th>
                  <th style={{ textAlign: 'right' }}>{t('எதிர்பார்ப்பு', 'Expected')}</th>
                  <th style={{ textAlign: 'right' }}>{t('செலுத்தியது', 'Paid')}</th>
                  <th style={{ textAlign: 'right' }}>{t('நடவடிக்கை', 'Action')}</th>
                </tr>
              </thead>
              <tbody>
                {cells.map((c) => (
                  <tr key={c.due_month}>
                    <td className="mono">{ledger_month_label(c.due_month)}</td>
                    <td>{ledger_status_label(c.status, lang)}</td>
                    <td style={{ textAlign: 'right' }} className="amount">
                      {c.status === 'no_row' ? '—' : format_inr(c.expected_amount_paise)}
                    </td>
                    <td style={{ textAlign: 'right' }} className="amount">
                      {c.status === 'no_row' ? '—' : format_inr(c.total_paid_paise)}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {c.status === 'paid' && <span style={{ color: 'var(--good)', fontSize: 12 }}>✓ {t('முழுமை', 'Paid')}</span>}
                      {c.subscription_id && c.status !== 'paid' && (
                        <button
                          className="btn sm primary"
                          onClick={() =>
                            set_pay_target({
                              subscription_id: c.subscription_id!,
                              expected: c.expected_amount_paise,
                              paid: c.total_paid_paise,
                              name: (lang === 'ta' ? sub?.name_ta : sub?.name_en) ?? '',
                            })
                          }
                        >
                          {t('செலுத்தல் புதுப்பி', 'Update payment')}
                        </button>
                      )}
                      {c.status === 'no_row' &&
                        (is_admin ? (
                          <button className="btn sm" disabled={gen_m.isPending} onClick={() => gen_m.mutate(c.due_month)}>
                            {gen_m.isPending ? '…' : t('கட்டணம் உருவாக்கு', 'Generate dues')}
                          </button>
                        ) : (
                          <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>{t('உருவாக்கப்படவில்லை', 'Not generated')}</span>
                        ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Payment history (this chit only) */}
          <div className="section-band">{t('செலுத்தல் வரலாறு', 'Payment History')}</div>
          <div className="card" style={{ padding: 0 }}>
            <table className="filings-table">
              <thead>
                <tr>
                  <th>{t('தேதி', 'Date')}</th>
                  <th style={{ textAlign: 'right' }}>{t('தொகை', 'Amount')}</th>
                  <th>{t('வழி', 'Mode')}</th>
                  <th>{t('குறிப்பு', 'Reference')}</th>
                  <th>{t('திரைப்பிடிப்பு', 'Screenshot')}</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.payment_id}>
                    <td className="mono">{new Date(p.timestamp).toLocaleDateString('en-IN')}</td>
                    <td style={{ textAlign: 'right' }} className="amount">{format_inr(p.amount_paise)}</td>
                    <td><span className="mode-tag">{p.method}</span></td>
                    <td className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>{p.reference ?? '—'}</td>
                    <td>
                      {p.screenshot_url ? (
                        <a href={p.screenshot_url} target="_blank" rel="noreferrer" className="btn ghost sm">{t('பார்', 'View')}</a>
                      ) : (
                        <span style={{ color: 'var(--ink-3)' }}>—</span>
                      )}
                    </td>
                  </tr>
                ))}
                {payments.length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', color: 'var(--ink-3)', padding: 18 }}>
                      {t('இன்னும் செலுத்தல் இல்லை', 'No payments yet')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {pay_target && (
        <MarkPaidModal
          subscription_id={pay_target.subscription_id}
          expected_paise={pay_target.expected}
          total_paid_paise={pay_target.paid}
          subscriber_name={pay_target.name}
          subscriber_id={subscriber_id}
          on_close={() => {
            set_pay_target(null);
            void qc.invalidateQueries({ queryKey: ['ledger-matrix', chit_id] });
            void qc.invalidateQueries({ queryKey: ['payment-history', subscriber_id, chit_id] });
          }}
        />
      )}
    </div>
  );
}
